import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Microscope, TestTube, Database, ShieldAlert, CheckCircle2,
  AlertCircle, ChevronDown, ChevronUp, Printer,
} from 'lucide-react';
import { apiListSamples, hasPhpBackend } from '@/lib/phpApi';
import { type DbSample, fmt, mapSampleToInfluent } from '@/lib/equipmentSpecUtils';
import ValidationPrintReport from './ValidationPrintReport';

export interface ValidationEquation {
  label: string;
  formula: string;
  substitution: string;
  result: string;
  status: 'pass' | 'warn' | 'fail';
  unit?: string;
}

export interface ValidationSection {
  title: string;
  icon: React.ElementType;
  equations: ValidationEquation[];
  warnings?: string[];
}

interface EquipmentAdminValidationProps {
  title: string;
  description: string;
  buildSections: (sample: Partial<Record<string, number>>) => ValidationSection[];
}

function Section({ title, icon: Icon, children, defaultOpen = false }: any) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="overflow-hidden rb-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-rb-elevated transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center"><Icon className="w-4 h-4 text-sky-400" /></div>
          <span className="text-sm font-bold text-white">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {open && <div className="p-4 border-t border-slate-800 space-y-3">{children}</div>}
    </div>
  );
}

function Equation({ label, formula, substitution, result, status, unit = '' }: ValidationEquation) {
  const statusColor = status === 'pass' ? 'text-emerald-400' : status === 'warn' ? 'text-amber-400' : 'text-rose-400';
  const StatusIcon = status === 'pass' ? CheckCircle2 : status === 'warn' ? AlertCircle : ShieldAlert;
  return (
    <div className="p-3 space-y-2 rb-inner-card-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-300">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold ${statusColor}`}>{result} {unit}</span>
          <StatusIcon className={`w-3.5 h-3.5 ${statusColor}`} />
        </div>
      </div>
      <div className="font-mono text-[11px] text-slate-500 bg-rb-surface rounded p-2">{formula}</div>
      <div className="font-mono text-[11px] text-sky-400 bg-rb-surface rounded p-2">{substitution}</div>
    </div>
  );
}

export default function EquipmentAdminValidation({ title, description, buildSections }: EquipmentAdminValidationProps) {
  const [samples, setSamples] = useState<DbSample[]>([]);
  const [selectedSampleId, setSelectedSampleId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!hasPhpBackend()) return;
    setLoading(true);
    apiListSamples().then((data: any) => {
      const s = Array.isArray(data?.samples) ? data.samples : Array.isArray(data) ? data : [];
      setSamples(s);
    }).catch(() => toast.error('Failed to load samples')).finally(() => setLoading(false));
  }, []);

  const selectedSample = samples.find((s) => s.id === selectedSampleId);
  const mappedParams = selectedSample?.params ? mapSampleToInfluent(selectedSample.params) : {};
  const sections = useMemo(() => buildSections(mappedParams), [mappedParams, buildSections]);

  const passCount = sections.reduce((acc, s) => acc + s.equations.filter((e) => e.status === 'pass').length, 0);
  const warnCount = sections.reduce((acc, s) => acc + s.equations.filter((e) => e.status === 'warn').length, 0);
  const failCount = sections.reduce((acc, s) => acc + s.equations.filter((e) => e.status === 'fail').length, 0);
  const totalChecks = passCount + warnCount + failCount;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
            <TestTube className="w-5 h-5 text-sky-400" />
          </div>
          {title}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{description}</p>
      </div>

      {/* Sample Selector */}
      <div className="p-4 rb-card">
        <div className="flex items-center gap-2 mb-3">
          <Database className="w-4 h-4 text-sky-400" />
          <h3 className="text-sm font-bold text-white">Select Database Sample to Validate</h3>
        </div>
        {loading ? (
          <p className="text-xs text-slate-500">Loading samples...</p>
        ) : samples.length === 0 ? (
          <p className="text-xs text-slate-500">No samples found in database.</p>
        ) : (
          <select
            value={selectedSampleId}
            onChange={(e) => setSelectedSampleId(e.target.value ? Number(e.target.value) : '')}
            className="w-full md:w-1/2 px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500 rb-inner-card-sm"
          >
            <option value="">— Select a recorded sample —</option>
            {samples.map((s) => (
              <option key={s.id} value={s.id}>
                {s.sample_name} {s.customer_name ? `(${s.customer_name})` : ''} — {s.created_at ? new Date(s.created_at).toLocaleDateString() : 'N/A'}
              </option>
            ))}
          </select>
        )}
      </div>

      {!selectedSampleId ? (
        <div className="p-8 text-center rb-card">
          <Microscope className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Select a sample above to view the full scientific validation.</p>
        </div>
      ) : (
        <>
          <div className="flex justify-end">
            <button
              onClick={() => setShowReport(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-slate-800 text-white border border-slate-700 hover:bg-slate-700 transition-colors"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
          </div>

          <ValidationPrintReport
            isOpen={showReport}
            onClose={() => setShowReport(false)}
            equipmentName={title.replace(' Validation — Admin Audit', '')}
            sampleName={selectedSample?.sample_name}
            customerName={selectedSample?.customer_name}
            date={new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            sections={sections}
            passCount={passCount}
            warnCount={warnCount}
            failCount={failCount}
            rawParams={mappedParams as Record<string, number>}
          />

        <div className="space-y-4">
          {/* Summary bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-rb-elevated rounded-lg p-3 border border-emerald-500/20">
              <p className="text-[10px] text-slate-500 uppercase font-bold">Checks Passed</p>
              <p className="text-lg font-bold text-emerald-400">{passCount} <span className="text-xs text-slate-500">/ {totalChecks}</span></p>
            </div>
            <div className="bg-rb-elevated rounded-lg p-3 border border-amber-500/20">
              <p className="text-[10px] text-slate-500 uppercase font-bold">Warnings</p>
              <p className="text-lg font-bold text-amber-400">{warnCount}</p>
            </div>
            <div className="bg-rb-elevated rounded-lg p-3 border border-rose-500/20">
              <p className="text-[10px] text-slate-500 uppercase font-bold">Failures</p>
              <p className="text-lg font-bold text-rose-400">{failCount}</p>
            </div>
          </div>

          {/* Raw Influent */}
          <Section title="0. Raw Influent Data" icon={Database} defaultOpen>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              {Object.entries(mappedParams).map(([key, val]) => {
                const labels: Record<string, string> = {
                  flowRate: 'Flow', tss: 'TSS', og: 'O&G', bod: 'BOD', cod: 'COD',
                  ph: 'pH', temp: 'Temp', alkalinity: 'Alkalinity', turbidity: 'Turbidity',
                  conductivity: 'Conductivity', hardness: 'Hardness', nh3n: 'NH₃-N', tp: 'TP',
                };
                const units: Record<string, string> = {
                  flowRate: 'm³/d', tss: 'mg/L', og: 'mg/L', bod: 'mg/L', cod: 'mg/L',
                  ph: '', temp: '°C', alkalinity: 'mg/L', turbidity: 'NTU',
                  conductivity: 'μS/cm', hardness: 'mg/L', nh3n: 'mg/L', tp: 'mg/L',
                };
                return (
                  <div key={key} className="bg-rb-surface rounded p-2">
                    <span className="text-slate-500">{labels[key] || key}:</span>{' '}
                    <span className="text-white font-bold">
                      {typeof val === 'number' ? fmt(val, key === 'ph' || key === 'tp' ? 1 : 0) : '—'} {units[key]}
                    </span>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Equipment-specific sections */}
          {sections.map((section, i) => (
            <Section key={i} title={`${i + 1}. ${section.title}`} icon={section.icon} defaultOpen>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.equations.map((eq, j) => (
                  <Equation key={j} {...eq} />
                ))}
              </div>
              {section.warnings && section.warnings.map((w, k) => (
                <div key={k} className="text-xs p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">
                  {w}
                </div>
              ))}
            </Section>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
